/**
 * Distributor-scoped pages for the Orders listing.
 *
 * Mirrors Vue ERP's `URL_FILTER_MAP` (`src/types/orders/order-constants.ts:481-797`),
 * which appends an "extra filter" — `distributorId == <id>` (or
 * `distributorGroupId == <id>` for parent-group pages like plain `amazon`) —
 * to the order search request based on the active route name. The Vue side
 * keys that map by route NAME (`amazonCanada`); we key by URL path because
 * React Router doesn't carry an equivalent route-name concept here.
 *
 * Source-of-truth distributor IDs come from Vue's `DISTRIBUTOR_IDS`
 * constant (`src/types/orders/order-constants.ts:451-479`). Inline values
 * are used where `URL_FILTER_MAP` hardcodes the ID (Brady, Singer
 * Equipment, the CW sub-distributors). Keep this map in sync when the
 * Vue side changes.
 *
 * Adding a new distributor page:
 *   1. Add a row to `REGISTRY` below. The wildcard route in
 *      `AppRoutes.tsx` (`/orders/sales-orders/*` → `<DistributorOrComingSoon>`)
 *      will pick up the path automatically — no route registration needed.
 *   2. `Orders.tsx` reads the rest (search-request `distributor` filter,
 *      breadcrumb trail, document title).
 */

import { OPERATORS, type FilterOperator } from 'constants/filterOperators';

/**
 * Backend attributes used by the order search filter DSL.
 * `distributorId` targets a single distributor; `distributorGroupId`
 * targets a parent group that spans multiple sub-distributors.
 */
export const DISTRIBUTOR_ATTRIBUTES = {
  DISTRIBUTOR_ID: 'distributorId',
  DISTRIBUTOR_GROUP_ID: 'distributorGroupId',
} as const;

type DistributorAttribute =
  (typeof DISTRIBUTOR_ATTRIBUTES)[keyof typeof DISTRIBUTOR_ATTRIBUTES];

export interface DistributorContext {
  /** Wire ID — matches Vue's `DISTRIBUTOR_IDS.<KEY>` or a hardcoded value in `URL_FILTER_MAP`. */
  id: string;
  /** Which backend column to filter on. */
  attribute: DistributorAttribute;
  /** Filter operator — always `EQUALS` for distributor scoping today. */
  operator: FilterOperator;
  /** Visible leaf label rendered in the breadcrumb and `<title>`.   */
  leafLabel: string;
  /** Parent-group label (e.g. "Amazon"). Used as a middle breadcrumb crumb. */
  parentLabel: string;
  /** Parent-group click target — used by the breadcrumb so users can
   *  navigate up. Empty string means non-navigable. */
  parentPath: string;
}

const ID = DISTRIBUTOR_ATTRIBUTES.DISTRIBUTOR_ID;
const GROUP_ID = DISTRIBUTOR_ATTRIBUTES.DISTRIBUTOR_GROUP_ID;

/**
 * Source-of-truth lookup. Keyed by exact pathname so router-driven
 * lookups are O(1) and additions are local. Grouped by parent label
 * for readability; ordering does not affect behaviour.
 *
 * Each entry mirrors a `URL_FILTER_MAP[<routeName>]` entry from
 * `RW-ERP-Service/src/types/orders/order-constants.ts`. See the
 * Vue-route-name comment above each row.
 */
const REGISTRY = {
  // ── Admin ─────────────────────────────────────────────────────────
  // Vue route name `admin` → DISTRIBUTOR_IDS.ADMIN, filtered on
  // `distributorGroupId` (`URL_FILTER_MAP.admin`,
  // `order-constants.ts:885-893`). Was previously miswired to `ID`
  // here, which pins the wrong column and returns no/incorrect rows.
  '/orders/sales-orders/admin': {
    id: '725bca7f-2510-4107-a4a0-57054656a688',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Admin',
    parentLabel: 'Sales orders',
    parentPath: '',
  },

  // ── Amazon ────────────────────────────────────────────────────────
  // Vue route name `amazon` → DISTRIBUTOR_IDS.AMAZON. Parent-group page:
  // shows every Amazon sub-distributor's orders via `distributorGroupId`
  // (`URL_FILTER_MAP.amazon`, `order-constants.ts:606-614`).
  '/orders/sales-orders/amazon': {
    id: '61137f14-e877-11ef-9aab-021882adbe2d',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Amazon',
    parentLabel: 'Sales orders',
    parentPath: '',
  },
  // Vue route name `amazonCanada` → DISTRIBUTOR_IDS.AMAZON_CANADA
  '/orders/sales-orders/amazon/amazon-canada': {
    id: 'da955d48-e931-11ef-9aab-021882adbe2d',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Amazon Canada',
    parentLabel: 'Amazon',
    parentPath: '',
  },
  // Vue route name `amazonDf` → DISTRIBUTOR_IDS.AMAZON_DF
  '/orders/sales-orders/amazon/amazon-df': {
    id: '0e321e02-612a-4080-95cd-e7093d22e101',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Amazon DF',
    parentLabel: 'Amazon',
    parentPath: '',
  },
  // Vue route name `amazonPo` → DISTRIBUTOR_IDS.AMAZON_PO
  '/orders/sales-orders/amazon/amazon-po': {
    id: 'da955c51-e931-11ef-9aab-021882adbe2d',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Amazon PO',
    parentLabel: 'Amazon',
    parentPath: '',
  },
  // Vue route name `amazonSeller` → DISTRIBUTOR_IDS.AMAZON_SELLER
  '/orders/sales-orders/amazon/amazon-seller-central': {
    id: '0e321e02-612a-4080-95cd-e7093d22e104',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Amazon Seller Central',
    parentLabel: 'Amazon',
    parentPath: '',
  },

  // ── Baldor ────────────────────────────────────────────────────────
  // Vue route name `baldor` → hardcoded group value in URL_FILTER_MAP
  // (no DISTRIBUTOR_IDS entry).
  '/orders/sales-orders/baldor': {
    id: 'c6652b40-b84b-4393-bc74-5f212d726792',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Baldor',
    parentLabel: 'Sales orders',
    parentPath: '',
  },
  // Baldor DF / Baldor Warehouse: Vue route names `baldorDf` /
  // `baldorWarehouse` → `DISTRIBUTOR_IDS.BALDOR_DF` /
  // `DISTRIBUTOR_IDS.BALDOR_WAREHOUSE`, both filtered on `distributorId`
  // (`URL_FILTER_MAP.baldorDf` / `.baldorWarehouse`,
  // `order-constants.ts:894-911`).
  '/orders/sales-orders/baldor/baldor-df': {
    id: '8f9521c8-211e-4527-b23f-ac953b0cf6e3',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Baldor DF',
    parentLabel: 'Baldor',
    parentPath: '',
  },
  '/orders/sales-orders/baldor/baldor-warehouse': {
    id: 'c6652b40-b84b-4c05-a496-85abbfdb0308',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Baldor Warehouse',
    parentLabel: 'Baldor',
    parentPath: '',
  },

  // ── Brady ─────────────────────────────────────────────────────────
  // Vue route name `brady` → hardcoded value (no DISTRIBUTOR_IDS entry).
  '/orders/sales-orders/brady': {
    id: '2fefe1bb-c60f-4035-ab56-e00016491347',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Brady',
    parentLabel: 'Sales orders',
    parentPath: '',
  },

  // ── Compass ───────────────────────────────────────────────────────
  // Vue route name `compass` → DISTRIBUTOR_IDS.COMPASS
  '/orders/sales-orders/compass': {
    id: '3843daaa-4ec2-4829-bbb2-fd34ccd79970',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Compass',
    parentLabel: 'Sales orders',
    parentPath: '',
  },

  // ── Chef's Warehouse ──────────────────────────────────────────────
  // Vue route name `chefsWarehouse` → hardcoded group value.
  '/orders/sales-orders/chefswarehouse': {
    id: '306caf80-e92f-11ef-9aab-021882adbe2d',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'ChefsWarehouse',
    parentLabel: 'Sales orders',
    parentPath: '',
  },
  // Vue route name `cw` → hardcoded value.
  '/orders/sales-orders/chefswarehouse/cw': {
    id: 'df02ade5-1794-43f9-8972-683041cd2701',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'CW',
    parentLabel: 'CW',
    parentPath: '',
  },
  // Vue route name `cwDirect` → hardcoded value (no DISTRIBUTOR_IDS entry).
  '/orders/sales-orders/chefswarehouse/cw-direct': {
    id: 'da954909-e931-11ef-9aab-021882adbe2d',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'CW Direct',
    parentLabel: 'CW',
    parentPath: '',
  },
  // Vue route name `cwDropship` → hardcoded value.
  '/orders/sales-orders/chefswarehouse/cw-dropship': {
    id: 'da9556b4-e931-11ef-9aab-021882adbe2d',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'CW DropShip',
    parentLabel: 'CW',
    parentPath: '',
  },
  // Vue route name `cwStock` → hardcoded value.
  '/orders/sales-orders/chefswarehouse/cw-stock': {
    id: 'd9bf952a-207c-43e3-ae16-2971b0acf0a5',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'CW Stock',
    parentLabel: 'CW',
    parentPath: '',
  },

  // ── Four Seasons ──────────────────────────────────────────────────
  // Vue route name `fourSeasons` → DISTRIBUTOR_IDS.FOUR_SEASONS
  '/orders/sales-orders/four-seasons': {
    id: '306cb8ed-e92f-11ef-9aab-021882adbe2d',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Four Seasons',
    parentLabel: 'Sales orders',
    parentPath: '',
  },

  // ── GFS ───────────────────────────────────────────────────────────
  // Vue route name `gfs` → DISTRIBUTOR_IDS.GFS. Parent-group page,
  // `distributorGroupId` (`URL_FILTER_MAP.gfs`, `order-constants.ts:687-695`).
  '/orders/sales-orders/gfs': {
    id: '3843daaa-4ec2-4829-bbb2-fd34ccd79969',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'GFS',
    parentLabel: 'Sales orders',
    parentPath: '',
  },
  // Vue route name `gfsCanada` → DISTRIBUTOR_IDS.GFS_CANADA
  '/orders/sales-orders/gfs/gfs-canada': {
    id: '0e321e02-612a-4080-95cd-33ed203d8558',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'GFS Canada',
    parentLabel: 'GFS',
    parentPath: '',
  },
  // Vue route name `gfsStores` → DISTRIBUTOR_IDS.GFS_STORES
  '/orders/sales-orders/gfs/gfs-stores': {
    id: '0e321e02-612a-4080-95cd-1bba63b0e703',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'GFS Stores',
    parentLabel: 'GFS',
    parentPath: '',
  },
  // Vue route name `gfsUS` → DISTRIBUTOR_IDS.GFS_US
  '/orders/sales-orders/gfs/gfs-us': {
    id: '0e321e02-612a-4080-95cd-bb7b5c910281',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'GFS US',
    parentLabel: 'GFS',
    parentPath: '',
  },

  // ── Legends ───────────────────────────────────────────────────────
  // Vue route name `legends` → DISTRIBUTOR_IDS.LEGENDS
  '/orders/sales-orders/legends': {
    id: '725bca7f-2510-4107-a4a0-57054656a672',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Legends',
    parentLabel: 'Sales orders',
    parentPath: '',
  },

  // ── Northwood ─────────────────────────────────────────────────────
  // Vue route name `northwood` → DISTRIBUTOR_IDS.NORTHWOOD, filtered on
  // `distributorGroupId` (`URL_FILTER_MAP.northwood`,
  // `order-constants.ts:912-920`). Previous entry here used both the
  // wrong attribute (`ID`) and an unrelated GUID — corrected to match
  // the Vue source verbatim.
  '/orders/sales-orders/northwood': {
    id: 'c6652b40-b84b-4393-bc74-5f212d726794',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Northwood',
    parentLabel: 'Sales orders',
    parentPath: '',
  },

  // ── Produce Pro ───────────────────────────────────────────────────
  // Vue route name `producePro` → DISTRIBUTOR_IDS.PRODUCE_PRO
  '/orders/sales-orders/produce-pro': {
    id: '725bca7f-2510-4107-a4a0-57054656a675',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Produce Pro',
    parentLabel: 'Sales orders',
    parentPath: '',
  },

  // ── Singer Equipment ──────────────────────────────────────────────
  // Vue route name `singerEquipment` → hardcoded value.
  '/orders/sales-orders/singer-equipment': {
    id: '306caf80-e92f-11ef-9aab-a0d298462225',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Singer Equipment',
    parentLabel: 'Sales orders',
    parentPath: '',
  },

  // ── Sodexo ────────────────────────────────────────────────────────
  // Vue route name `sodexo` → DISTRIBUTOR_IDS.SODEXO
  '/orders/sales-orders/sodexo': {
    id: '3843daaa-4ec2-4829-bbb2-fd34ccd79972',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Sodexo',
    parentLabel: 'Sales orders',
    parentPath: '',
  },

  // ── Storefront ────────────────────────────────────────────────────
  // Vue route name `storeFront` → DISTRIBUTOR_IDS.STORE_FRONT, filtered
  // on `distributorGroupId` (`URL_FILTER_MAP.storeFront`,
  // `order-constants.ts:876-884`).
  '/orders/sales-orders/store-front': {
    id: '725bca7f-2510-4107-a4a0-57054656a687',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Storefront',
    parentLabel: 'Sales orders',
    parentPath: '',
  },

  // ── Sysco ─────────────────────────────────────────────────────────
  // Vue route name `sysco` → DISTRIBUTOR_IDS.SYSCO. Parent-group page,
  // `distributorGroupId` (`URL_FILTER_MAP.sysco`, `order-constants.ts:804-812`).
  '/orders/sales-orders/sysco': {
    id: '7047ef0a-7484-4e4d-9a22-87e9c5e53759',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Sysco',
    parentLabel: 'Sales orders',
    parentPath: '',
  },
  // Vue route name `europeanImports` → DISTRIBUTOR_IDS.EUROPEAN_IMPORTS
  '/orders/sales-orders/sysco/european-imports': {
    id: '1318c385-4ce8-49eb-8f49-3de0e4251859',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'European Imports',
    parentLabel: 'Sysco',
    parentPath: '',
  },
  // Vue route name `sotf` → DISTRIBUTOR_IDS.SOTF
  '/orders/sales-orders/sysco/sotf': {
    id: '0e321e02-612a-4080-95cd-e7093d22e109',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'SOTF',
    parentLabel: 'Sysco',
    parentPath: '',
  },
  // Vue route name `syscoDirect` → DISTRIBUTOR_IDS.SYSCO_DIRECT
  '/orders/sales-orders/sysco/sysco-direct': {
    id: 'e55b1c1f-612a-4080-95cd-dbfe28ce8a95',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Sysco Direct',
    parentLabel: 'Sysco',
    parentPath: '',
  },

  // ── Tao Group ─────────────────────────────────────────────────────
  // Vue route name `taoGroup` → DISTRIBUTOR_IDS.TAO_GROUP
  '/orders/sales-orders/tao-group': {
    id: '725bca7f-2510-4107-a4a0-57054656a677',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Tao Group',
    parentLabel: 'Sales orders',
    parentPath: '',
  },

  // ── US Foods ──────────────────────────────────────────────────────
  // Vue route name `usFoods` → DISTRIBUTOR_IDS.USFOODS. Parent-group
  // page, `distributorGroupId` (`URL_FILTER_MAP.usFoods`,
  // `order-constants.ts:651-659`).
  '/orders/sales-orders/us-foods': {
    id: '3843daaa-4ec2-4829-bbb2-fd34ccd79971',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'US Foods',
    parentLabel: 'Sales orders',
    parentPath: '',
  },
  // Vue route name `usFoodsUS` → DISTRIBUTOR_IDS.USFOODS_US
  '/orders/sales-orders/us-foods/us': {
    id: '41372ec3-1d07-4292-b96e-98e2669e1e81',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'US Foods (US)',
    parentLabel: 'US Foods',
    parentPath: '',
  },
  // Vue route name `usFoodsCs` → DISTRIBUTOR_IDS.USFOODS_CS
  '/orders/sales-orders/us-foods/us-foods-cs': {
    id: 'da955873-e931-11ef-9aab-021882adbe2d',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'US Foods CS',
    parentLabel: 'US Foods',
    parentPath: '',
  },
  // Vue route name `usFoodsDirect` → DISTRIBUTOR_IDS.USFOODS_DIRECT
  '/orders/sales-orders/us-foods/us-foods-direct': {
    id: 'da955924-e931-11ef-9aab-021882adbe2d',
    attribute: ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'US Foods Direct',
    parentLabel: 'US Foods',
    parentPath: '',
  },

  // ── Zoro ──────────────────────────────────────────────────────────
  // Vue route name `zoro` → DISTRIBUTOR_IDS.ZORO, filtered on
  // `distributorGroupId` (`URL_FILTER_MAP.zoro`,
  // `order-constants.ts:807-814`) — same shape as Tao Group / Northwood.
  '/orders/sales-orders/zoro': {
    id: 'e222d461-11b1-460b-af99-5d03d13217f2',
    attribute: GROUP_ID,
    operator: OPERATORS.EQUALS,
    leafLabel: 'Zoro',
    parentLabel: 'Sales orders',
    parentPath: '',
  },
} as const satisfies Record<string, DistributorContext>;

/** Narrows a pathname to one the registry knows. */
function isRegisteredPath(pathname: string): pathname is keyof typeof REGISTRY {
  return Object.hasOwn(REGISTRY, pathname);
}

/**
 * Look up the distributor context for a given pathname.
 * Returns `null` for the generic `/orders` and `/orders/sales-orders/all`
 * routes — those render the unscoped listing with no extra filter — and
 * for paths that are not yet wired (those routes fall back to
 * `<ComingSoon />` via `DistributorOrComingSoon`).
 */
export function getDistributorContext(
  pathname: string,
): DistributorContext | null {
  return isRegisteredPath(pathname) ? REGISTRY[pathname] : null;
}

/**
 * Predicate equivalent of `getDistributorContext` — used by the
 * `/orders/sales-orders/*` route wrapper to decide whether to render
 * `<Orders />` or `<ComingSoon />` for an arbitrary sub-path.
 */
export function isDistributorPath(pathname: string): boolean {
  return isRegisteredPath(pathname);
}
